import { createReducer } from 'store/createReducer';
import { AddonGroup } from 'models/AddonGroup';
import {
  SELECT_ADDON,
  AddonAction,
  UNSELECT_ADDON,
  RESET_ADDON_GROUP,
  AddonGroupAction
} from './actions';
import {
  PRODUCT_PAGE_VARIANT_DATA_SET,
  ProductPageVariantDataAction
} from 'views/ProductPage/actions';
import { RelatedProduct } from 'models/RelatedProduct';
import { hasElements } from 'utils';

const selectAddon =
  (addonId: string, amount: number) =>
  (addon: RelatedProduct): RelatedProduct =>
    addon.productId === addonId && addon.quantity.amount === amount
      ? { ...addon, selected: true }
      : { ...addon, selected: false };

const unselectAddon = (addon: RelatedProduct): RelatedProduct => ({
  ...addon,
  selected: false
});
export const addons = createReducer<{ [key: string]: AddonGroup }>(
  {},
  {
    [RESET_ADDON_GROUP]: (state, { groupKey }: AddonGroupAction) => {
      const group = state[groupKey];
      return {
        ...state,
        [groupKey]: {
          ...group,
          products: group.products.map(unselectAddon)
        }
      };
    },
    [SELECT_ADDON]: (state, { groupKey, productId, amount }: AddonAction) => {
      const currentGroup = state[groupKey];
      return {
        ...state,
        [groupKey]: {
          ...currentGroup,
          products: currentGroup.products.map(selectAddon(productId, amount))
        }
      };
    },
    [UNSELECT_ADDON]: (state, { groupKey }: AddonAction) => {
      const currentGroup = state[groupKey];
      return {
        ...state,
        [groupKey]: {
          ...currentGroup,
          products: currentGroup.products.map(unselectAddon)
        }
      };
    },
    [PRODUCT_PAGE_VARIANT_DATA_SET]: (state, { data }: ProductPageVariantDataAction) => {
      const { addons: newAddons } = data;
      if (!newAddons) {
        return {};
      }

      const addons = Object.keys(newAddons).reduce<{ [key: string]: AddonGroup }>(
        (addonGroups, key) => {
          const newAddonGroup = newAddons[key];
          const oldAddonGroup = state[key];

          // If we have a new addon group, i.e it did not have products in the old state
          // or did not exists (old variant does not have this addon group), return
          // with the new addonGroup
          const isNewAddonGroup =
            oldAddonGroup === undefined || !hasElements(oldAddonGroup.products);

          if (isNewAddonGroup) {
            return { ...addonGroups, [key]: newAddonGroup };
          }

          // If the addon group existed and had products, we want to map through em
          // and "merge" it with the new group, to retain `selected` state for each
          // product in the addon group
          return {
            ...addonGroups,
            [key]: {
              ...newAddonGroup,
              products: newAddonGroup.products.map(newProduct => {
                const oldProduct = oldAddonGroup.products.find(
                  oldProduct => oldProduct.productId === newProduct.productId
                );
                const selected =
                  oldProduct !== undefined ? oldProduct.selected : newProduct.selected;
                // Update the product since quantity might change, but retain the selected state
                return {
                  ...newProduct,
                  selected
                };
              })
            }
          };
        },
        {}
      );
      return addons;
    }
  }
);
